import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { GroupFilters } from '@shared/models/group-filters';
import { defaultPaginationState, PaginationState } from '@shared/models/pagination-state';
import { loadCustomers, loadCustomersFailure, loadCustomersSuccess } from '../actions/load-customers.actions';
import { loadCustomer, loadCustomerSuccess, loadCustomerFailure } from '../actions/load-customer.actions';
import { createCustomer, createCustomerSuccess, createCustomerFailure } from '../actions/create-customer.actions';
import { updateCustomer, updateCustomerFailure, updateCustomerSuccess } from '../actions/update-customer.actions';
import { selectAddCustomerParentGroup } from '../actions/select-add-customer-parent-group.actions';
import { Sorting } from '@shared/models/sorting';
import { LoadingState } from '@shared/types/loading-state';
import {
  resendEmailConfirmation,
  resendEmailConfirmationFailure,
  resendEmailConfirmationSuccess,
} from '../../shared/actions/resend-email-confirmation.actions';
import {
  loadPartnerDetails,
  loadPartnerDetailsFailure,
  loadPartnerDetailsSuccess,
} from '@features/customers/partner-details/shared/actions/load-partner-details.actions';
import { setGroupFilters } from '../actions/set-group-filters.actions';
import {
  updateRenewalAsAService,
  updateRenewalAsAServiceSuccess,
  updateRenewalAsAServiceFailure,
} from '@features/customers/renewal-as-a-service/actions/update-renewal-as-a-service.actions';
import { Group } from '@shared/models/group';
import { selectCustomerForUpdate } from '../actions/select-customer-for-update.actions';
import { selectAllCustomersForUpdate } from '../actions/select-all-customers-for-update.actions';
import {
  batchUpdateCustomers,
  batchUpdateCustomersFailure,
  batchUpdateCustomersSuccess,
} from '../actions/batch-update-customers.actions';
import {
  updateExternalCustomerSubscriptions,
  updateExternalCustomerSubscriptionsFailure,
  updateExternalCustomerSubscriptionsSuccess,
} from '../actions/update-external-customer-subscriptions.actions';

export const featureKey = 'customers';

export interface State extends EntityState<Group> {
  paginationState: PaginationState;
  filters: GroupFilters;
  sorting?: Sorting;
  loadingState: LoadingState;
  loadingText?: string;
  addCustomerParentGroup?: Partial<Group>;
  allCustomersCheckedForUpdate?: boolean;
}

export const customersAdapter = createEntityAdapter<Group>({
  selectId: (group: Group) => group.id,
});

export const initialState: State = customersAdapter.getInitialState({
  paginationState: defaultPaginationState,
  filters: { active: true },
  loadingState: 'not_loading',
});

export const customersReducer = createReducer(
  initialState,
  on(
    loadCustomers,
    loadCustomer,
    createCustomer,
    updateCustomer,
    batchUpdateCustomers,
    resendEmailConfirmation,
    loadPartnerDetails,
    updateExternalCustomerSubscriptions,
    (state) => ({
      ...state,
      loadingState: 'loading' as LoadingState,
    }),
  ),
  on(createCustomer, (state) => ({
    ...state,
    loadingText: `Creating Customer`,
  })),
  on(updateCustomer, (state) => ({
    ...state,
    loadingText: 'Saving changes...',
  })),
  on(batchUpdateCustomers, (state) => ({
    ...state,
    loadingText: 'Updating Customers...',
  })),
  on(resendEmailConfirmation, (state) => ({
    ...state,
    loadingText: 'Resending Email Confirmation',
  })),
  on(updateExternalCustomerSubscriptions, (state) => ({
    ...state,
    loadingText: 'Syncing External Subscriptions...',
  })),
  on(setGroupFilters, (state, { filters }) => ({
    ...state,
    filters: filters,
  })),
  on(updateRenewalAsAService, (state) => ({
    ...state,
    loadingState: 'loading' as LoadingState,
  })),
  on(updateRenewalAsAServiceSuccess, (state, { renewalAsAService }) => {
    let group = state.entities[renewalAsAService.attributes.group_id];
    if (group) {
      return customersAdapter.updateOne(
        {
          id: group.id,
          changes: {
            ...group,
            attributes: {
              ...group.attributes,
              renewal_as_a_service: renewalAsAService,
            },
          },
        },
        { ...state, loadingState: 'loaded' as LoadingState, loadingText: undefined },
      );
    } else {
      return { ...state, loadingState: 'loaded' as LoadingState, loadingText: undefined };
    }
  }),
  on(updateRenewalAsAServiceFailure, (state) => ({
    ...state,
    loadingState: 'not_loading' as LoadingState,
  })),
  on(loadCustomersSuccess, (state, { data, paginationState, filters, sorting }): State => {
    return customersAdapter.setAll(data, {
      ...state,
      paginationState,
      loadingState: 'loaded' as LoadingState,
      loadingText: undefined,
      filters: filters,
      sorting: sorting,
    });
  }),
  on(loadCustomerSuccess, createCustomerSuccess, updateCustomerSuccess, (state, { group }): State => {
    return customersAdapter.upsertOne(group, {
      ...state,
      loadingState: 'loaded' as LoadingState,
      loadingText: undefined,
    });
  }),
  on(
    resendEmailConfirmationSuccess,
    loadPartnerDetailsSuccess,
    batchUpdateCustomersSuccess,
    updateExternalCustomerSubscriptionsSuccess,
    (state) => ({
      ...state,
      loadingState: 'loaded' as LoadingState,
      loadingText: undefined,
    }),
  ),
  on(
    loadCustomersFailure,
    loadCustomerFailure,
    createCustomerFailure,
    updateCustomerFailure,
    batchUpdateCustomersFailure,
    resendEmailConfirmationFailure,
    loadPartnerDetailsFailure,
    updateExternalCustomerSubscriptionsFailure,
    (state) => ({
      ...state,
      loadingState: 'not_loading' as LoadingState,
      loadingText: undefined,
    }),
  ),
  on(selectAddCustomerParentGroup, (state, { group }) => ({
    ...state,
    addCustomerParentGroup: group,
  })),
  on(selectCustomerForUpdate, (state, { checked, groupId }) => {
    let group = state.entities[groupId];
    if (!group) return { ...state };

    return customersAdapter.updateOne(
      {
        id: group.id,
        changes: { ...group, checked: checked },
      },
      checked ? { ...state } : { ...state, allCustomersCheckedForUpdate: false },
    );
  }),
  on(selectAllCustomersForUpdate, (state, { checked, allCustomers }) => {
    const entities = Object.values(state.entities);

    return customersAdapter.updateMany(
      entities.map((customer) => ({ id: customer!.attributes.id, changes: { checked: checked } })),
      {
        ...state,
        allCustomersCheckedForUpdate: checked && allCustomers,
      },
    );
  }),
);

export function reducer(state: State | undefined, action: Action) {
  return customersReducer(state, action);
}

export const customerFeatureSelector = createFeatureSelector<State>(featureKey);

const { selectIds, selectEntities, selectAll, selectTotal } = customersAdapter.getSelectors(customerFeatureSelector);

export const selectAllGroups = selectAll;

export const selectCustomersLoadingState = createSelector(
  customerFeatureSelector,
  (state: State) => state.loadingState,
);
export const selectCustomersLoadingText = createSelector(customerFeatureSelector, (state: State) => state.loadingText);

export const selectCustomersPaginationState = createSelector(
  customerFeatureSelector,
  (state: State) => state.paginationState,
);

export const selectCustomerById = (groupId: string) =>
  createSelector(selectEntities, (entities) => (groupId == undefined ? undefined : entities[groupId]));

export const selectRenewalAsAServiceByCustomerId = (groupId: string) =>
  createSelector(selectEntities, (entities) =>
    groupId == undefined ? undefined : entities[groupId]?.attributes?.renewal_as_a_service,
  );

export const selectAddCustomerParentGroupAttributes = createSelector(customerFeatureSelector, (state: State) => {
  return state.addCustomerParentGroup;
});

export const selectGroupFilters = createSelector(customerFeatureSelector, (state) => state.filters);

export const selectGroupSorting = createSelector(customerFeatureSelector, (state) => state.sorting);

export const selectCheckedCustomersForUpdate = createSelector(selectAll, (groups) =>
  groups.filter((group) => group.checked),
);

export const selectAllCustomersCheckedForUpdate = createSelector(
  customerFeatureSelector,
  (state) => state.allCustomersCheckedForUpdate,
);
